import { useEffect, useRef, useState } from 'react'
import { withDebounce } from '../utils'

/**
 * Custom hook that measures the height of a container element and returns it.
 *
 * @template T The type of the container element.
 * @returns An object containing the container height and a ref to the container element.
 */
export const useResizeObserver = <T extends HTMLDivElement>() => {
    const treeContainerRef = useRef<T | null>(null)
    const [containerHeight, setContainerHeight] = useState(0)

    // side-effect to measure height dynamically
    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null
        let isMounted = true
        let rafId: number | null = null

        const handleResize = withDebounce<ResizeObserverCallback>(
            (observer) => {
                if (observer[0] && isMounted) {
                    const { height } = observer[0].contentRect
                    // Use requestAnimationFrame to defer state update to next frame
                    // This prevents "ResizeObserver loop completed with undelivered notifications" error
                    // by allowing the browser to complete its layout pass before React re-renders
                    if (rafId !== null) {
                        cancelAnimationFrame(rafId)
                    }
                    rafId = requestAnimationFrame(() => {
                        if (isMounted) {
                            setContainerHeight(height)
                        }
                        rafId = null
                    })
                }
            }
        )

        try {
            resizeObserver = new ResizeObserver(handleResize)

            if (treeContainerRef.current) {
                resizeObserver.observe(treeContainerRef.current)
            }
        } catch (error) {
            // noop
        }

        return () => {
            isMounted = false
            handleResize.cancel()
            // Cancel any pending animation frame to prevent memory leaks
            if (rafId !== null) {
                cancelAnimationFrame(rafId)
            }
            if (resizeObserver && treeContainerRef?.current) {
                resizeObserver.unobserve(treeContainerRef.current)
                resizeObserver.disconnect()
            }
        }
    }, [])

    return {
        treeContainerRef,
        containerHeight,
    }
}
